import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/*
  Recently viewed products are only known client-side (localStorage), so this
  mirrors product-recommendations.js's own pattern: render a skeleton/hidden
  shell server-side, then once this element scrolls into view, ask Shopify's
  real /search endpoint to resolve those ids into real product data (the same
  trick predictive-search.js already uses for its "recently viewed" dropdown
  list - see assets/predictive-search.js #getRecentlyViewedProductsMarkup)
  and morph the section in with the result.
*/
class RecentlyViewedProductsLoader extends Component {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#load();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
  }

  async #load() {
    const { sectionId, resultsPerformed, searchUrl, currentProductId } = this.dataset;
    if (!sectionId || !searchUrl || resultsPerformed === 'true') return;

    const viewedProducts = RecentlyViewed.getProducts().filter((id) => id !== currentProductId);
    if (viewedProducts.length === 0) {
      this.remove();
      return;
    }

    const url = new URL(searchUrl, location.origin);
    url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`Server returned ${response.status}`);

      const html = await response.text();
      if (!html.trim().length) {
        this.remove();
        return;
      }

      // 'hydration' mode (used by product-recommendations.js) only morphs
      // elements carrying a data-hydration-key attribute, which this section
      // doesn't set - 'full' morphs the whole tree unconditionally, which is
      // what's needed here to swap the skeleton for real cards.
      morphSection(sectionId, html, { mode: 'full', injectStylesheet: true });
    } catch (error) {
      console.error('Recently viewed products error:', error);
      this.remove();
    }
  }
}

if (!customElements.get('recently-viewed-products-loader')) {
  customElements.define('recently-viewed-products-loader', RecentlyViewedProductsLoader);
}
